import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import React, {forwardRef, useMemo, useRef} from 'react';
import {Components, Virtuoso, VirtuosoHandle} from 'react-virtuoso';

import {SectionSettingsContextProvider} from '../WorkspaceDrawer/Settings/SectionSettingsContextProvider';
import {AddSectionButton} from './AddSectionButton';
import {EmptyPanelBankMessage, EmptyPanelSearchMessage} from './EmptyMessages';
import {usePanelBankConfigWithRefs} from './hooks/usePanelBankConfigWithRefs';
import {SortablePanelBankSection} from './SortablePanelBankSection';
import {SectionWithRef} from './types';
import {useHandleDragEnd} from './useHandleDragEnd';
import {getDndSortableSectionId} from './utils';
import {WorkspaceSectionContextProvider} from './WorkspaceSectionContextProvider';

type VirtuosoContext = {
  isDraggingSection: boolean;
  isSearching: boolean;
  numVisibleSections: number;
};

const virtuosoComponents: Components<SectionWithRef, VirtuosoContext> = {
  Footer: forwardRef<HTMLDivElement, {context?: VirtuosoContext}>(
    ({context}, ref) => {
      // TODO - add back in
      // const {numVisibleSections, isDraggingSection} = context!;

      // if (!numVisibleSections) {
      //   return null;
      // }

      return (
        <div
          ref={ref}
          style={{
            padding: '1rem 2rem',
            display: 'flex',
            // TODO - add back in
            // display: isDraggingSection ? 'none' : 'flex',
            justifyContent: 'flex-end',
          }}>
          <AddSectionButton />
        </div>
      );
    }
  ),
  EmptyPlaceholder: forwardRef<HTMLDivElement, {context?: VirtuosoContext}>(
    ({context}, ref) => {
      const {isSearching} = context!;

      return (
        <div ref={ref} style={{height: '100%'}}>
          {isSearching ? (
            <EmptyPanelSearchMessage />
          ) : (
            <EmptyPanelBankMessage />
          )}
        </div>
      );
    }
  ),
};

/**
 * This component is currently being built in parallel to the existing PanelBankSections component. This is currently behind a statsig gate.
 * https://console.statsig.com/2qsBpsviQSxHWKIyMZHZzm/gates?Tag=2TAfQEBRDyRI5yekkM1kA
 */
export const PanelBankSections = ({
  isProjectReadOnly,
}: {
  isProjectReadOnly: boolean;
}) => {
  const virtuosoRef = useRef<VirtuosoHandle | null>(null);

  // TODO - add back useScrollToPanelBankSectionId

  // TODO - add data dog logging

  // This is our source of truth since it's what usePanelBankSectionList uses to filter on
  const panelBankConfigWithRefs = usePanelBankConfigWithRefs();

  const sections: SectionWithRef[] = panelBankConfigWithRefs.sections;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const items = useMemo(() => {
    return sections.map(s => getDndSortableSectionId(s.ref));
  }, [sections]);

  const handleDragEnd = useHandleDragEnd();

  return (
    <DndContext
      sensors={sensors}
      // We use the default  "closest corners" collision detection since it's more aligned with
      // what the human eye would predict since the drag handle is in the top left corner.
      onDragEnd={handleDragEnd}>
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <Virtuoso
          components={virtuosoComponents}
          computeItemKey={idx => getDndSortableSectionId(sections[idx].ref)}
          data={sections}
          itemContent={(idx, sectionWithRef) => {
            return (
              <WorkspaceSectionContextProvider
                sectionName={sectionWithRef.name}>
                <SectionSettingsContextProvider sectionRef={sectionWithRef.ref}>
                  <SortablePanelBankSection
                    idx={idx}
                    isProjectReadOnly={isProjectReadOnly}
                    numSections={sections.length}
                    sectionWithRef={sectionWithRef}
                  />
                </SectionSettingsContextProvider>
              </WorkspaceSectionContextProvider>
            );
          }}
          ref={virtuosoRef}
          style={{flexGrow: 1}}
          useWindowScroll
        />
      </SortableContext>
    </DndContext>
  );
};
